import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import ConversationsDropdown from "./ConversationsDropdown";
import { ContactThing } from "./ContactLabel.tsx";
import { Conversation, Message } from "../utils/Types.ts";
import { Card } from "@/components/ui/card.tsx";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../utils/AuthContext.tsx";
import { useBreadcrumbs } from "./BreadcrumbContext.tsx";
import AppBreadcrumb from "./AppBreadcrumb";
import { useConversationParticipants, useConversation, useMessages, useCreateMessage } from "../utils/Api.ts";

const Messaging: React.FC<{}> = () => {
  const { conversationId } = useParams();
  const [selectedConversationId, setSelectedConversationId] = useState<string | undefined>(conversationId);
  const navigate = useNavigate();
  const { token } = useAuth();
  const { updateBreadcrumbs } = useBreadcrumbs();
  const initialBreadcrumb = [{ name: "Messaging", path: "/sources/messaging" }];
  const { data: participants } = useConversationParticipants(selectedConversationId, token ?? undefined);
  const { data: conversation } = useConversation(selectedConversationId, token ?? undefined);
  const {
    data: messages = [],
    isFetching,
    //refetch: refetchMessages,
    error,
  } = useMessages(selectedConversationId, token ?? undefined);

  const sortedMessages = useMemo(() => {
    return [...messages].reverse();
  }, [messages]);

  useEffect(() => {
    updateBreadcrumbs(initialBreadcrumb);
  }, []);

  useEffect(() => {
    if (conversation) {
      updateBreadcrumbs([
        ...initialBreadcrumb,
        {
          name: `${conversation.name}`,
          path: `/sources/messaging/${selectedConversationId}`,
        },
      ]);
    }
  }, [conversation]);

  // Handle selection change from the dropdown
  const handleSelectionChange = (conversation?: Conversation) => {
    setSelectedConversationId(conversation?.id);
    navigate(`/sources/messaging/${conversation?.id}`);
  };

  const { mutate: createMessage, isPending: isCreating } = useCreateMessage(token ?? undefined);
  const handleSend = async (body: string) => {
    const message: Message = {
      id: `temp-${Date.now()}`,
      conversation: selectedConversationId!,
      content: body,
      type: "sms",
      previousId: "",
      created: new Date(),
      from: "+12408001666", //only for display while processing
      attachments: 0,
      sending: true,
      error: false,
      multiline: body.indexOf("\n") > 0,
    };
    createMessage(message);
  };

  return (
    <div className="w-full overflow-y-scroll">
      <div className="flex flex-row items-center pt-2 h-[72px] sm:h-[96px] pl-1">
        <AppBreadcrumb />
      </div>
      <div className="flex flex-col pt-2 space-y-4 grow w-full max-w-[540px]">
        <ConversationsDropdown onSelectionChange={handleSelectionChange} defaultValue={selectedConversationId} />
        {conversation && (
          <div className="pt-10 pl-2">
            <MessageList messages={sortedMessages} loading={isFetching} error={error?.message} />
            <MessageInput onSend={handleSend} disabled={isCreating} />
            <div className="pb-32"></div>
          </div>
        )}
      </div>
      <Sheet open={selectedConversationId != undefined} modal={false}>
        <SheetContent
          className="py-24 hidden shadow-none lg:inline md:[&>button]:hidden lg:max-w-[360px] lg:px-8 xl:max-w-[640px] xl:pl-12"
          side="right"
        >
          <SheetHeader>
            <SheetTitle></SheetTitle>
            <h4 className="scroll-m-20 text-xl font-semibold tracking-tight mb-4">Participants</h4>
            <SheetDescription>This conversation includes the following people</SheetDescription>
          </SheetHeader>

          {/* list of participants */}
          <div className="m-auto grid grid-cols-3 pt-8 gap-3">
            {participants?.map((p, index) => (
              <Card className="rounded-sm p-2" key={index}>
                <ContactThing identity={p.id} />
              </Card>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Messaging;
